import { ApiMetadataRetriever, ModelRow } from "@mcleod/core";
import { RowWebMenuGroup, RowWebMenuGroupType } from "../../models/ModelWebMenuGroup";
import { RowWebMenuItem } from "../../models/ModelWebMenuItem";
import { ReadOnlyTextAreaPanel } from "../ReadOnlyTextAreaPanel";
import { MenuAction } from "./MenuAction";
import { MenuActionSQLUtils } from "./MenuActionSQLUtils";

export class MenuActionViewChanges extends MenuAction {
    private deleteItemStatements: string[] = [];
    private deleteGroupStatements: string[] = [];
    private addedWebMenuItems: UpdatedWebMenuItem[] = [];
    private addedWebMenuGroups: UpdatedWebMenuGroup[] = [];
    private updatedWebMenuItems: UpdatedWebMenuItem[] = [];
    private updatedWebMenuGroups: UpdatedWebMenuGroup[] = [];

    override validate(): boolean {
        return this.menuDataSource.hasChanged();
    }

    override async internalExecute(): Promise<boolean> {
        await ApiMetadataRetriever.get("common/web-menu-group");
        await ApiMetadataRetriever.get("common/web-menu-item");

        this.collectAllChanges();
        this.displayChangesInDialog();
        // returning true would indicate data has changed and the menu tree would be rebuilt
        return false;
    }

    private collectAllChanges() {
        // Clear previous collections
        this.deleteItemStatements = [];
        this.deleteGroupStatements = [];
        this.addedWebMenuItems = [];
        this.addedWebMenuGroups = [];
        this.updatedWebMenuItems = [];
        this.updatedWebMenuGroups = [];

        // Use Maps to track unique items/groups by ID to avoid duplicates
        const uniqueItems = new Map<string, UpdatedWebMenuItem>();
        const uniqueGroups = new Map<string, UpdatedWebMenuGroup>();

        // Collect delete statements
        this.populateDeleteStatements();

        // Collect all changes from the menu tree
        this.menuDataSource.menuData.recurseItems(child => {
            this.processMenuItemChanges(child, uniqueItems, uniqueGroups);
        });

        // Convert Maps back to arrays
        this.addedWebMenuItems = Array.from(uniqueItems.values()).filter(item => item.isAppending());
        this.updatedWebMenuItems = Array.from(uniqueItems.values()).filter(item => !item.isAppending());
        this.addedWebMenuGroups = Array.from(uniqueGroups.values()).filter(group => group.isAppending());
        this.updatedWebMenuGroups = Array.from(uniqueGroups.values()).filter(group => !group.isAppending());
    }

    private processMenuItemChanges(child: any, uniqueItems: Map<string, UpdatedWebMenuItem>, uniqueGroups: Map<string, UpdatedWebMenuGroup>) {
        // Process item data changes
        if (child.itemData.hasChanged()) {
            const itemId = child.itemData.get("id");
            if (!uniqueItems.has(itemId)) {
                uniqueItems.set(itemId, new UpdatedWebMenuItem(child.itemData));
            }
        }

        // Process group data changes (main group structure)
        if (child.groupData.hasChanged()) {
            const groupId = child.groupData.get("id");
            if (!uniqueGroups.has(groupId)) {
                uniqueGroups.set(groupId, new UpdatedWebMenuGroup(child.groupData));
            }
        }

        // Process group reference data changes (shared menu references)
        if (child.groupReferenceData?.hasChanged()) {
            const groupRefId = child.groupReferenceData.get("id");
            if (!uniqueGroups.has(groupRefId)) {
                uniqueGroups.set(groupRefId, new UpdatedWebMenuGroup(child.groupReferenceData));
            }
        }
    }

    private populateDeleteStatements() {
        this.menuDataSource.deletedItems.forEach((item) => {
            this.deleteItemStatements.push(this.getDeleteStatement(item, "web_menu_item"));
        });

        this.menuDataSource.deletedGroups.forEach((group) => {
            this.deleteGroupStatements.push(this.getDeleteStatement(group, "web_menu_group"));
        });
    }

    private getDeleteStatement(model: ModelRow, tableName: string): string {
        return MenuActionSQLUtils.createDeleteStatement(tableName, model);
    }

    private generateOrderedScripts(): string[] {
        const scripts: string[] = [];

        // 1. web_menu_item deletes
        if (this.deleteItemStatements.length > 0) {
            scripts.push("-- Delete web_menu_item records --");
            scripts.push(...this.deleteItemStatements);
            scripts.push("");
        }

        // 2. web_menu_group deletes
        if (this.deleteGroupStatements.length > 0) {
            scripts.push("-- Delete web_menu_group records --");
            scripts.push(...this.deleteGroupStatements);
            scripts.push("");
        }

        // 3. web_menu_item inserts
        if (this.addedWebMenuItems.length > 0) {
            scripts.push("-- Insert new web_menu_item records --");
            scripts.push(...this.addedWebMenuItems.map(item => item.getScript()));
            scripts.push("");
        }

        // 4. web_menu_group inserts
        if (this.addedWebMenuGroups.length > 0) {
            scripts.push("-- Insert new web_menu_group records --");
            scripts.push(...this.addedWebMenuGroups.map(group => group.getScript()));
            scripts.push("");
        }

        // 5. web_menu_item updates
        if (this.updatedWebMenuItems.length > 0) {
            scripts.push("-- Update existing web_menu_item records --");
            scripts.push(...this.updatedWebMenuItems.map(item => item.getScript()));
            scripts.push("");
        }

        // 6. web_menu_group updates
        if (this.updatedWebMenuGroups.length > 0) {
            scripts.push("-- Update existing web_menu_group records --");
            scripts.push(...this.updatedWebMenuGroups.map(group => group.getScript()));
            scripts.push("");
        }

        return scripts;
    }

    private displayChangesInDialog() {
        const scripts = this.generateOrderedScripts();
        const scriptText = scripts.join("\n");
        ReadOnlyTextAreaPanel.showInDialog(scriptText, { title: "Pending Menu Changes - SQL Scripts"});
    }
}

// Base class for tracking changes to menu items and groups
abstract class UpdatedModelRow<T extends ModelRow> {
    protected model: T;
    protected updateMessages: string[];

    constructor(model: T) {
        this.model = model;
        this.updateMessages = this.generateUpdateMessages();
    }

    abstract getTableName(): string;
    abstract getScript(): string;

    getEnglish(): string {
        return this.updateMessages.join("\n");
    }

    isAppending(): boolean {
        return this.model._appending;
    }

    protected generateUpdateMessages(): string[] {
        if (this.model._appending) {
            return [`${this.getDisplayName()} was added`];
        }

        const updateMessages = [];
        const meta = this.model.getMetadata();
        for (const [key, value] of Object.entries(this.model.data)) {
            if (value !== this.model.originalData?.[key]) {
                const field = meta.output[key];
                const caption = field?.caption ?? key;
                const english = `${caption} was updated from ${this.model.originalData[key]} to ${value}`;
                updateMessages.push(english);
            }
        }
        return updateMessages;
    }

    protected abstract getDisplayName(): string;

    protected getInsertScript(): string {
        const insertData = MenuActionSQLUtils.getInsertData(this.model);
        return MenuActionSQLUtils.createInsertStatement(this.getTableName(), insertData);
    }

    protected getUpdateScript(): string {
        const updateData = MenuActionSQLUtils.getUpdateData(this.model);
        return MenuActionSQLUtils.createUpdateStatement(this.getTableName(), updateData, this.model);
    }
}

class UpdatedWebMenuItem extends UpdatedModelRow<RowWebMenuItem> {
    constructor(item: RowWebMenuItem) {
        super(item);
    }

    getTableName(): string {
        return "web_menu_item";
    }

    getScript(): string {
        if (this.model._appending) {
            return this.getInsertScript();
        }
        return this.getUpdateScript();
    }

    protected getDisplayName(): string {
        return this.model.get("caption") || "Menu Item";
    }
}

class UpdatedWebMenuGroup extends UpdatedModelRow<RowWebMenuGroup> {
    constructor(group: RowWebMenuGroup) {
        super(group);
    }

    getTableName(): string {
        return "web_menu_group";
    }

    getScript(): string {
        if (this.model._appending) {
            return this.getInsertScript();
        }
        return this.getUpdateScript();
    }

    protected getDisplayName(): string {
        const groupType = this.model.get("group_type");
        if (groupType === RowWebMenuGroupType.SHARED) {
            return "Shared Menu Group";
        } else if (groupType === RowWebMenuGroupType.SHARED_REFERENCE) {
            return "Shared Menu Reference";
        }
        return "Menu Group";
    }
}
